/**
 * triggerAnalysis — Fire-and-forget forensic roast generation at save time.
 *
 * Called immediately after a verdict is persisted to Firestore.
 * Calls generateForensicRoastV2 Cloud Function and writes the result
 * to verdict.forensicRoast. On failure, writes a deterministic template
 * fallback so the user always sees analysis on re-open.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { updateVerdict } from '@/services/verdicts';
import type { VerdictInputs, VerdictCalculations, UserProfile, ForensicRoastData } from '@/types';

interface TriggerAnalysisParams {
  verdictId: string;
  userId: string;
  inputs: VerdictInputs;
  calculations: VerdictCalculations;
  profile: UserProfile | null;
}

/** Deterministic hash of inputs that affect the analysis. */
function computeInputHash(inputs: VerdictInputs, calculations: VerdictCalculations): string {
  const parts = [
    inputs.medium,
    inputs.width,
    inputs.height,
    inputs.hours,
    inputs.materials,
    inputs.gutPrice ?? 0,
    inputs.tier,
    calculations.ttcp,
    calculations.ceiling,
  ];
  return parts.join('|');
}

/** Template fallback — always produces content. */
function getTemplateFallback(
  inputs: VerdictInputs,
  calculations: VerdictCalculations,
): Omit<ForensicRoastData, 'generatedAt' | 'inputHash'> {
  const floor = Math.round(calculations.ttcp / 100);
  const asking = inputs.gutPrice ? Math.round(inputs.gutPrice / 100) : floor;
  const delta = asking - floor;
  const pct = floor > 0 ? Math.round((delta / floor) * 100) : 0;

  let status: ForensicRoastData['status'];
  let analysis: string;

  if (delta < 0) {
    status = 'Below Floor';
    analysis = `At $${asking}, this piece is **${Math.abs(pct)}% below** your breakeven floor of $${floor}. Every hour spent on this work costs you money at this price point. The floor accounts for your survival rate, materials, and time — pricing below it means you're subsidizing the buyer.`;
  } else if (pct <= 30) {
    status = 'Supported';
    analysis = `At $${asking}, this piece clears your breakeven floor of $${floor} by **${pct}%**. This is a defensible price point — it covers your costs and provides a modest margin. The pricing is sustainable for your current burn rate.`;
  } else {
    status = 'Above Market Band';
    analysis = `At $${asking}, this piece is **${pct}% above** your breakeven floor of $${floor}. While ambitious, this price may face resistance unless your market position supports it. Monitor sell-through at this level.`;
  }

  return { analysis, status, isEstimated: true };
}

/**
 * Fire-and-forget: triggers the V2 forensic roast Cloud Function and persists
 * the result to verdict.forensicRoast. On any failure, writes a deterministic
 * template fallback so the analysis section always has content.
 */
export async function triggerAnalysisAtSave({
  verdictId,
  userId,
  inputs,
  calculations,
  profile,
}: TriggerAnalysisParams): Promise<void> {
  const inputHash = computeInputHash(inputs, calculations);
  const isPersonalized = !!(
    profile &&
    profile.monthlyBurn > 0 &&
    profile.monthlyCapacity > 0 &&
    profile.monthlyBurn !== 300000 &&
    profile.monthlyCapacity !== 130
  );

  const payload = {
    medium: inputs.medium,
    height: inputs.height,
    width: inputs.width,
    hours: inputs.hours,
    materials: inputs.materials,
    gutPrice: inputs.gutPrice ?? 0,
    tier: inputs.tier,
    floor: calculations.ttcp,
    ceiling: calculations.ceiling,
    delta: calculations.delta ?? 0,
    effRate: calculations.effectiveHourly ?? 0,
    survivalRate: calculations.survivalTarget,
    isPersonalized,
    tracked: inputs.hoursTracked ?? false,
    careerStage: inputs.careerStage,
    monthlyBurn: inputs.monthlyBurn,
  };

  let roastData: ForensicRoastData;

  try {
    const callFn = httpsCallable<
      { verdict: typeof payload; subscriptionStatus?: string },
      { analysis: string; status: string; isEstimated: boolean } | { error: string }
    >(functions, 'generateForensicRoastV2');

    const result = await callFn({
      verdict: payload,
      subscriptionStatus: profile?.subscriptionStatus ?? 'free',
    });

    const data = result.data;
    if ('error' in data) {
      console.warn('[triggerAnalysis] Cloud Function returned error:', data.error);
      const fallback = getTemplateFallback(inputs, calculations);
      roastData = { ...fallback, generatedAt: new Date().toISOString(), inputHash };
    } else {
      roastData = {
        analysis: data.analysis,
        status: data.status as ForensicRoastData['status'],
        isEstimated: data.isEstimated,
        generatedAt: new Date().toISOString(),
        inputHash,
      };
    }
  } catch (err) {
    console.warn('[triggerAnalysis] Cloud Function failed, using template:', err);
    const fallback = getTemplateFallback(inputs, calculations);
    roastData = { ...fallback, generatedAt: new Date().toISOString(), inputHash };
  }

  // Always persist — either AI or template. Analysis section always has content.
  try {
    await updateVerdict(userId, verdictId, { forensicRoast: roastData });

    // Bump verdictsVersion so portfolio reloads from Firestore
    const currentVersion = parseInt(sessionStorage.getItem('verdictsVersion') || '0', 10);
    sessionStorage.setItem('verdictsVersion', String(currentVersion + 1));
  } catch (err) {
    console.warn('[triggerAnalysis] Failed to persist forensicRoast:', err);
  }
}
